import * as React from 'react';
import { connect } from 'react-redux';
import { isTablet } from 'react-device-detect';
import {
    Button,
    ButtonGroup,
    CloseButton,
    Icons,
    Panel,
    PanelBody,
    PanelFooter,
    PanelHeader,
    Preloader
} from 'components';
import { IState } from 'services/root-reducer';
import { actions as containersActions } from 'services/containers/containers-service';
import { EOperationStatus, IAppConfig } from 'types';
import {
    BaseLayerCode,
    ComparableLayers,
    favouritesContainersCodes,
    FolderCode,
    pdfContainerCodes
} from 'project-constants';
import {
    getFavorites,
    getHints,
    getLayerIcon,
    getLayerObject,
    getOmjsContainer,
    getOmjsLayerConfig,
    getOmjsViewInstance,
    getOperationData,
    getOperationStatus,
    getTopContainer
} from 'selectors';
import { actions as appActions } from 'services/app/app-service';
import { actions as mapActions } from 'services/map/map-service';
import { actions as favoritesActions } from 'services/favorites/favorites-service';
import { actions as overlayWindowsActions } from 'services/overlay-windows/overlay-windows-service';
import { formatApiUrl, ObjectsHelper } from 'utils';

import './style.scss';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { IObjectRouteParams, routes } from 'routes';
import clsx from 'clsx';
import i18next from 'i18next';
import { WithTranslation, withTranslation } from 'react-i18next';
import { HelpButtonWithPopover } from 'components/help-button';
import { ObjectGenPlanyInfo } from './object-info';

interface IObjectWindowGenPlanyOwnProps extends RouteComponentProps<IObjectRouteParams>, WithTranslation {
    zoomOnMount?: boolean;
}


export interface IObjectWindowGenPlanyProps extends IObjectWindowGenPlanyOwnProps {
    allContainers: IState['operations']['CONTAINERS']['data'];
    genPlanes: IState['operations']['GEN_PLANES']['data'];
    gpStatus: EOperationStatus;
    folderState: any;
    apiUrlWithVersion: string;
    config: IAppConfig;
    favorites: IState['favorites']['items'];
    isInFavorites: boolean;
    hints: any[];
    layerCode: string;
    objectId: number;
    object: ReturnType<typeof getLayerObject>;
    layerConfig: ReturnType<typeof getOmjsLayerConfig>;
    currentZoom: number;
}

export interface IObjectWindowGenPlanyState {
    showPowers: boolean;
}

const mapStateToProps = (state: IState, ownProps: IObjectWindowGenPlanyOwnProps) => {
    let { match } = ownProps;
    const objectId = parseInt(match.params.objectId);
    const { layerCode } = match.params;

    let { config } = state.app;
    let favorites = getFavorites(state);
    let in_favorites = favorites[layerCode]?.includes(objectId) ?? false;

    return {
        allContainers: getOperationData(state, 'CONTAINERS'),
        genPlanes: getOperationData(state, 'GEN_PLANES'),
        gpStatus: getOperationStatus(state, 'GEN_PLANES'),
        apiUrlWithVersion: formatApiUrl(config.apiUrl),
        folderState: getOperationData(state, 'FOLDER_STATE'),
        config: config,
        favorites: getFavorites(state),
        isInFavorites: in_favorites,
        hints: getHints(state),
        layerCode: layerCode,
        objectId: objectId,
        object: getLayerObject(state, layerCode, objectId),
        layerConfig: getOmjsLayerConfig(state, layerCode),
        currentZoom: getOmjsViewInstance(state).getMap().getView().getZoom()
    };
};

class ObjectWindowComponent extends React.Component<IObjectWindowGenPlanyProps, IObjectWindowGenPlanyState> {
    /**
     * Флаг/костыль для зума к объекту при инициализации компонента после загрузки всех данных.
     * false означает что зумится не надо
     * true означает что как только сработает componenDidUpdate будет вызван метод для зума к объекту
     */
    private _zoomOnMountNotResolved = false;

    constructor(props: IObjectWindowGenPlanyProps) {
        super(props);

        this.state = this.getInitialState();

        this.onCloseClick = this.onCloseClick.bind(this);
        this.onPDFClick = this.onPDFClick.bind(this);
        this.onLinkClick = this.onLinkClick.bind(this);
        this.onZoomToObjectClick = this.onZoomToObjectClick.bind(this);
        this.onFavoritesClick = this.onFavoritesClick.bind(this);
        this.onDataUpdateClick = this.onDataUpdateClick.bind(this);
    }

    getInitialState(showPowers?: boolean) {
        return {
            showPowers: showPowers || false
        };
    }

    componentDidMount() {
        let { zoomOnMount = false } = this.props;
        let { layerCode, objectId } = this.props.match.params;
        if (isTablet) {
            appActions.hideObjectFiltersWindow();
        }
        if (!ComparableLayers.has(layerCode)) {
            mapActions.getConnections([ 0, 0 ]);
        } else {
            mapActions.getConnectionsByObject({
                layerCode: layerCode,
                objectId: parseInt(objectId)
            });
        }

        if (zoomOnMount) {
            this._zoomOnMountNotResolved = true;
        }
    }

    componentDidUpdate(prevProps: Readonly<IObjectWindowGenPlanyProps>) {
        let { layerCode, objectId } = this.props.match.params;
        if (isTablet) {
            appActions.hideObjectFiltersWindow();
        }
        if (!ComparableLayers.has(layerCode)) {
            mapActions.getConnections([ 0, 0 ]);
        }

        let { layerCode: prevLayerCode, objectId: prevObjectId } = prevProps.match.params;
        if (layerCode !== prevLayerCode || objectId !== prevObjectId) {
            /* eslint-disable-next-line */
            this.setState(this.getInitialState());

            if (ComparableLayers.has(layerCode)) {
                mapActions.getConnectionsByObject({
                    layerCode: layerCode,
                    objectId: parseInt(objectId)
                });
            }
        }

        if (this._zoomOnMountNotResolved) {
            this._zoomOnMountNotResolved = false;
            this.onZoomToObjectClick();
        }
    }

    getGenplanContainer() {
        return this.props.allContainers.find(c => c.code === FolderCode['GEN_PLANS_OBJECTS']);
    }

    onPDFClick() {
        let {
            apiUrlWithVersion,
            config,
            i18n,
            layerCode,
            objectId
        } = this.props;

        let topContainer = getTopContainer(false, layerCode);

        if (!topContainer) {
            console.warn('[onPDFClick]', { topContainer });
            return;
        }

        // код оригинального слоя, по которому созданы виртуальные слои категорий
        let baseLayerCode = BaseLayerCode[topContainer.get('code')] || layerCode;

        let path = `${apiUrlWithVersion + config.mapCode}/layers/${baseLayerCode}/objects/${objectId}/`;
        let query = `f=pdf&custom=1&lng=${i18n.language}&dl=1`;

        window.open(`${path}?${query}`);
    }

    onLinkClick() {
        let { layerCode, objectId } = this.props;
        let object_link = ObjectsHelper.getObjectLink({
            layer_code: layerCode,
            object_id: objectId
        });

        overlayWindowsActions.showObjectLinkOverlay(object_link);
    }

    onCloseClick(event: React.MouseEvent) {
        mapActions.getConnections([ 0, 0 ]);
        while (routes.navigation.length && routes.navigation[routes.navigation.length - 1].params.objectId) {
            routes.navigation.pop();
        }
        routes.goBack(routes.map, { container: 'root' }, routes.currRouteState);
    }

    onZoomToObjectClick() {
        let { layerCode, objectId, object } = this.props;
        appActions.hideObjectFiltersWindow();
        let cont = getOmjsContainer(false, layerCode, true);

        if (!cont || !object) {
            console.warn('[onZoomToObjectClick]', { cont, object });
            return;
        }

        if (cont.get('render_type') === 0) {
            const objGeom = object.getGeometry();
            const geom = new omjs.ol.geom[objGeom.type](objGeom.coordinates);
            mapActions.fitToExtent(geom.getExtent());
        } else {
            mapActions.fitToTarget({
                layer: layerCode,
                objectId: object.id
            });
        }
    }

    onFavoritesClick() {
        let { isInFavorites, objectId, layerCode } = this.props;
        if (isInFavorites) {
            favoritesActions.removeItem({ layer_code: layerCode, object_id: objectId });
        } else {
            favoritesActions.addItem({ layer_code: layerCode, object_id: objectId });
        }
    }

    onDataUpdateClick() {
        let { layerCode, objectId, layerConfig, object } = this.props;

        if (!layerConfig || !object) {
            console.warn('[onDataUpdateClick]', { layerConfig, object });
            return;
        }

        overlayWindowsActions.showUpdateDataOverlay({
            layer_code: layerCode,
            object_id: objectId,
            object_name: ObjectsHelper.getObjectName(object, layerConfig),
            object_url: ObjectsHelper.getObjectLink({
                layer_code: layerCode,
                object_id: object.id
            })
        });
    }

    renderPreloader() {
        return (
            <Panel className={clsx('object-window-wrapper', 'preloader-wrapper')}>
                <Preloader />
            </Panel>
        );
    }

    renderError() {
        let { t } = this.props;
        return (
            <Panel className={clsx('object-window-wrapper', 'error')}>
                <PanelHeader className="object-window-header">
                    <div className="window-close">
                        <CloseButton
                            onClick={this.onCloseClick}
                        />
                    </div>
                </PanelHeader>
                <PanelBody className='object-window-body'>
                    <div className='error-message'>
                        {t('Ошибка загрузки')}
                    </div>
                    {/* <div>
                        <LinkButton
                            className='retry-button'
                            onClick={this.loadObjectData}
                            label={t('Повторить')}
                        />
                    </div> */}
                </PanelBody>
            </Panel>
        );
    }

    renderHelpButton(containerCode: string) {
        if (containerCode.indexOf('plots') === 0 || containerCode.indexOf('parks') === 0) {
            const hint = this.props.hints.find(obj => obj.get('code') === 'info_window');
            if (hint) {
                const { title, text } = hint['_attributes'];
                return (
                    <HelpButtonWithPopover
                        className="for-objects-window"
                        popoverProps={{
                            placement: 'bottom',
                            className: 'help-button-popover bottom-placement'
                        }}
                        offset={[ 0, 24 ]}
                    >
                        <div className="header">
                            {title}
                        </div>
                        <div className="body">
                            {text}
                        </div>
                    </HelpButtonWithPopover>
                );
            }
        }

        return null;
    }

    render() {
        let {
            allContainers,
            isInFavorites,
            folderState,
            t,
            layerCode,
            objectId,
            object,
            layerConfig,
            currentZoom,
            gpStatus
        } = this.props;
        let container = this.getGenplanContainer();

        if (!container || !object || !layerConfig) {
            console.warn({ container, object, layerConfig });
            return this.renderError();
        }

        let objectName = ObjectsHelper.getObjectName(object, layerConfig);
        let rootContainer = allContainers.find(c => c.name === objectName);


        if (gpStatus === EOperationStatus.PENDING) {
            return this.renderPreloader();
        }

        if (!rootContainer && currentZoom >= 12) {
            console.warn({ rootContainer });
            return this.renderError();
        }

        return (
            <Panel className={clsx('object-window-wrapper', 'gen-plany-window-wrapper')}>
                <div className="left-block">
                    <PanelHeader className="object-window-header">
                        <div className="window-icon">
                            <img
                                alt=""
                                src={getLayerIcon(false, {
                                    layer_code: layerCode,
                                    object_id: objectId
                                })}
                            />
                        </div>
                        <div
                            className="window-title"
                            dangerouslySetInnerHTML={{
                                __html: objectName
                            }}
                        />
                        {this.renderHelpButton(layerCode)}
                        <div className="window-close">
                            <CloseButton
                                onClick={this.onCloseClick}
                            />
                        </div>
                    </PanelHeader>
                    {currentZoom < 12 && (
                        <div className="gen-plany-zoom-message">
                            {i18next.t('Для управления слоями приблизьтесь к территории')}
                        </div>
                    )}
                    {currentZoom >= 12 && (
                        <PanelBody className='object-window-body'>
                            <div className="object-data">
                                <ObjectGenPlanyInfo
                                    currentZoom={currentZoom}
                                    allContainers={allContainers}
                                    folderState={folderState}
                                    rootContainer={rootContainer}
                                    onContainerExpand={(id, isChecked) => {
                                        appActions.setFolderState({
                                            id: id, state: isChecked
                                        });
                                    }}
                                    onSelectedChange={(id, isSelected) => {
                                        containersActions.setContainerSelected({
                                            id_or_code: id, selected: isSelected
                                        });
                                    }}
                                />
                            </div>
                        </PanelBody>
                    )}
                    <PanelFooter className='object-window-footer'>
                        <ButtonGroup>
                            <Button
                                variant='blue'
                                onClick={this.onZoomToObjectClick}
                                icon={<Icons.IconMagnifyGlassPlus />}
                                text={t('Приблизить')}
                            />
                            {
                                !__IS_CORDOVA__ && (
                                    <Button
                                        title={t('Ссылка на объект')}
                                        variant='blue'
                                        onClick={this.onLinkClick}
                                        icon={<Icons.IconLink />}
                                    />
                                )
                            }
                            {pdfContainerCodes.includes(layerCode) && (
                                <Button
                                    title={t('Скачать PDF')}
                                    variant="blue"
                                    onClick={this.onPDFClick}
                                    icon={<Icons.IconPdf />}
                                />
                            )}
                            {favouritesContainersCodes.includes(layerCode) && (
                                <>
                                    <Button
                                        title={i18next.t('Добавить в избранное')}
                                        variant="blue"
                                        onClick={this.onFavoritesClick}
                                        className={clsx({
                                            'in-favorites-icon-color': isInFavorites
                                        })}
                                        icon={<Icons.IconStar />}
                                    />
                                    <Button
                                        title={t('Заявка на обновление данных')}
                                        variant="blue"
                                        className='data-update-button'
                                        onClick={this.onDataUpdateClick}
                                        icon={<Icons.IconNote />}
                                    />
                                </>
                            )}
                        </ButtonGroup>
                    </PanelFooter>
                </div>
            </Panel>
        );
    }
}

export const ObjectWindowGenPlany = withRouter(
    // @ts-ignore
    withTranslation()(
        connect(mapStateToProps)(ObjectWindowComponent)
    )
);
